import { useState } from 'react';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Button } from '@/components/base/buttons/button';
import type { PricingSettings, CreatePricingSettingsData, UpdatePricingSettingsData, MarginType, SellRateComponent } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { X, Plus } from '@untitledui/icons';

interface PricingSettingsFormProps {
    settings?: PricingSettings;
    onSubmit: (data: CreatePricingSettingsData | UpdatePricingSettingsData) => void;
    onCancel: () => void;
}

export const PricingSettingsForm = ({ settings, onSubmit, onCancel }: PricingSettingsFormProps) => {
    const { t } = useTranslation('pages:quotes');
    const [formData, setFormData] = useState({
        buyRate: settings?.buyRate || 0,
        margin: {
            value: settings?.margin.value || 0,
            type: (settings?.margin.type || 'percentage') as MarginType,
        },
        sellRateComponents: settings?.sellRateComponents || [],
        baseCurrency: settings?.baseCurrency || 'EUR',
    });

    const marginTypeOptions = [
        { id: 'percentage', label: t('settings.pricing.marginType.percentage') },
        { id: 'currency', label: t('settings.pricing.marginType.currency') },
    ];

    const handleAddComponent = () => {
        setFormData({
            ...formData,
            sellRateComponents: [
                ...formData.sellRateComponents,
                {
                    id: `temp-${Date.now()}`,
                    name: '',
                    value: 0,
                    type: 'currency' as MarginType,
                    description: '',
                },
            ],
        });
    };

    const handleRemoveComponent = (id: string) => {
        setFormData({
            ...formData,
            sellRateComponents: formData.sellRateComponents.filter(comp => comp.id !== id),
        });
    };

    const handleComponentChange = (id: string, field: keyof SellRateComponent, value: any) => {
        setFormData({
            ...formData,
            sellRateComponents: formData.sellRateComponents.map(comp =>
                comp.id === id ? { ...comp, [field]: value } : comp
            ),
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const components = formData.sellRateComponents.map(({ id, ...rest }) => rest);
        onSubmit({
            ...formData,
            sellRateComponents: components,
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                    label={t('settings.pricing.form.buyRate')}
                    placeholder="0"
                    value={formData.buyRate.toString()}
                    onChange={(value) => setFormData({ ...formData, buyRate: parseFloat(value) || 0 })}
                    type="number"
                    isRequired
                    size="md"
                />

                <Select
                    label={t('settings.pricing.form.baseCurrency')}
                    items={[
                        { id: 'EUR', label: 'EUR' },
                        { id: 'USD', label: 'USD' },
                        { id: 'GBP', label: 'GBP' },
                    ]}
                    selectedKey={formData.baseCurrency}
                    onSelectionChange={(key) => setFormData({ ...formData, baseCurrency: key as string })}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                    label={t('settings.pricing.form.marginValue')}
                    placeholder="0"
                    value={formData.margin.value.toString()}
                    onChange={(value) => setFormData({
                        ...formData,
                        margin: { ...formData.margin, value: parseFloat(value) || 0 },
                    })}
                    type="number"
                    isRequired
                    size="md"
                />

                <Select
                    label={t('settings.pricing.form.marginType')}
                    items={marginTypeOptions}
                    selectedKey={formData.margin.type}
                    onSelectionChange={(key) => setFormData({
                        ...formData,
                        margin: { ...formData.margin, type: key as MarginType },
                    })}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-primary">
                        {t('settings.pricing.form.sellRateComponents')}
                    </label>
                    <Button
                        type="button"
                        onClick={handleAddComponent}
                        color="secondary"
                        size="sm"
                        iconLeading={Plus}
                    >
                        {t('settings.pricing.form.addComponent')}
                    </Button>
                </div>
                <div className="space-y-4">
                    {formData.sellRateComponents.map((component) => (
                        <div key={component.id} className="rounded-lg bg-secondary p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-primary">
                                    {component.name || t('settings.pricing.form.component')}
                                </h4>
                                <Button
                                    type="button"
                                    onClick={() => handleRemoveComponent(component.id)}
                                    color="primary-destructive"
                                    size="sm"
                                    iconLeading={X}
                                />
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <Input
                                    label={t('settings.pricing.form.componentName')}
                                    placeholder={t('settings.pricing.form.componentNamePlaceholder')}
                                    value={component.name}
                                    onChange={(value) => handleComponentChange(component.id, 'name', value)}
                                    size="md"
                                />
                                <Input
                                    label={t('settings.pricing.form.componentValue')}
                                    placeholder="0"
                                    value={component.value.toString()}
                                    onChange={(value) => handleComponentChange(component.id, 'value', parseFloat(value) || 0)}
                                    type="number"
                                    size="md"
                                />
                            </div>
                            <Select
                                label={t('settings.pricing.form.componentType')}
                                items={marginTypeOptions}
                                selectedKey={component.type}
                                onSelectionChange={(key) => handleComponentChange(component.id, 'type', key)}
                                size="md"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                            <Input
                                label={t('settings.pricing.form.componentDescription')}
                                placeholder={t('settings.pricing.form.componentDescriptionPlaceholder')}
                                value={component.description || ''}
                                onChange={(value) => handleComponentChange(component.id, 'description', value)}
                                size="md"
                            />
                        </div>
                    ))}
                    {formData.sellRateComponents.length === 0 && (
                        <p className="text-sm text-tertiary text-center py-4">
                            {t('settings.pricing.form.noComponents')}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary">
                <Button
                    type="button"
                    onClick={onCancel}
                    color="secondary"
                    size="md"
                >
                    {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                >
                    {settings ? t('buttons.update', { ns: 'common' }) : t('buttons.create', { ns: 'common' })}
                </Button>
            </div>
        </form>
    );
};

